<template>
	<v-card class="px-4 pt-2 pb-4">
		<v-card-title class="d-flex align-center justify-space-between">
			<label>
				{{ $t("LOCALIZATION_FILE") }}
			</label>
			<v-btn icon variant="flat" @click="$emit('close')">
				<v-icon> mdi-close </v-icon>
			</v-btn>
		</v-card-title>
		<v-card-text class="pt-1 pb-0 px-3 white-text">
			<v-container class="pa-0">
				<v-row class="d-block ma-0" style="width: 100%">
					<v-col>
						<!-- チェックボックス -->
						<v-row>
							<v-col cols="3" class="d-flex align-center mb-1 pa-0">
								<v-checkbox color="primary" class="mt-0 checkbox-label-size" v-model="isPointCloudChecked"
									:label="$t('POINT_CLOUD')" hide-details>
								</v-checkbox>
							</v-col>
							<v-col cols="3" class="d-flex align-center mb-1 pa-0">
								<v-checkbox color="primary" class="mt-0 checkbox-label-size" v-model="isDesignChecked"
									:label="$t('DESIGN')" hide-details>
								</v-checkbox>
							</v-col>
							<v-col cols="3" class="d-flex align-center mb-1 pa-0">
								<v-checkbox color="primary" class="mt-0 checkbox-label-size" v-model="isLineworksChecked"
									:label="$t('LINEWORKS')" hide-details>
								</v-checkbox>
							</v-col>
						</v-row>
						<!-- 測量データを選択 -->
						<v-row v-if="isPointCloudChecked">
							<v-col cols="12" class="mt-3 pa-0">
								<span>
									<label>{{ $t("SELECT_SURVEY_DATA") }} {{ pointCloudExtensionLabel }}</label>
									<v-select variant="outlined" :label="$t('ADD')" v-model="selectedPoints" density="compact" single-line
										bg-color="white" :items="pointCloudList" item-title="name" return-object hide-details multiple :no-data-text="$t('NO_DATA_AVAILABLE')">
										<template v-slot:selection="{ index }">
											<span class="text-black" v-if="index === 0">{{
												$t("ADD")
											}}</span>
										</template>
										<template v-slot:item="{ item, props }">
											<v-list-item v-bind="props" :disabled="isDisabledPointCloudSelectItem(item.raw)">
												<template v-slot:prepend="{ isActive }">
													<v-checkbox-btn color="primary" :model-value="isActive"></v-checkbox-btn>
												</template>
												<!-- 組が成立しないデータセットは理由を補助テキストで出す（GI-39） -->
												<template v-if="rejectionMessageKey(item.raw)" v-slot:subtitle>
													{{ $t(rejectionMessageKey(item.raw)) }}
												</template>
											</v-list-item>
										</template>
									</v-select>
								</span>
								<div v-if="isPointCloudChecked && selectedPoints.length">
									<div v-for="point in selectedPoints" :key="point.id" class="pt-2 d-flex justify-space-between"
										style="width: 95%">
										<span class="selected-list">{{ point.name }}</span>
										<close-button @click="deselectPoint(point.id)" size="20px"></close-button>
									</div>
								</div>
							</v-col>
							<!-- 軸を選択 -->
							<v-col v-show="isAxisEnabled" cols="12" class="mt-3 pa-0">
								<span>
									<label>{{ $t("axis") }}</label>
									<v-select variant="outlined" v-model="axis" bg-color="white" density="compact" :items="axisList"
										hide-details :no-data-text="$t('NO_DATA_AVAILABLE')">
									</v-select>
								</span>
							</v-col>
						</v-row>
						<!-- 設計データを選択 -->
						<v-row v-if="isDesignChecked">
							<v-col cols="12" class="mt-3 pa-0">
								<span>
									<label>{{ $t("SELECT_DESIGN_DATA") }} {{ designExtensionLabel }}</label>
									<v-select variant="outlined" :label="$t('ADD')" v-model="selectedDesigns" single-line bg-color="white"
										density="compact" :items="designList" item-title="name" return-object hide-details multiple :no-data-text="$t('NO_DATA_AVAILABLE')">
										<template v-slot:selection="{ index }">
											<span class="text-black" v-if="index === 0">{{
												$t("ADD")
											}}</span>
										</template>
									</v-select>
								</span>
								<div v-if="isDesignChecked && selectedDesigns.length">
									<div v-for="design in selectedDesigns" :key="design.id" class="pt-2 d-flex justify-space-between"
										style="width: 95%">
										<span class="selected-list">{{ design.name }}</span>
										<close-button @click="deselectDesign(design.id)" size="20px"></close-button>
									</div>
								</div>
							</v-col>
						</v-row>
						<!-- ラインワークスデータを選択 -->
						<v-row v-if="isLineworksChecked">
							<v-col cols="12" class="mt-3 pa-0">
								<span>
									<label>{{ $t("SELECT_LINEWORKS_DATA") }}</label>
									<v-select variant="outlined" :label="$t('ADD')" v-model="selectedLineworks" single-line
										bg-color="white" density="compact" :items="designList" item-title="name" return-object hide-details
										multiple :no-data-text="$t('NO_DATA_AVAILABLE')">
										<template v-slot:selection="{ index }">
											<span v-if="index === 0">{{
												$t("ADD")
											}}</span>
										</template>
									</v-select>
								</span>
								<div v-if="isLineworksChecked && selectedLineworks.length">
									<div v-for="lineworks in selectedLineworks" :key="lineworks.id"
										class="pt-2 d-flex justify-space-between" style="width: 95%">
										<span class="selected-list">{{ lineworks.name }}</span>
										<close-button @click="deselectLineworks(lineworks.id)" size="20px"></close-button>
									</div>
								</div>
							</v-col>
						</v-row>
						<!-- アップロードボタン -->
						<v-row>
							<v-col cols="4" class="mt-3 mb-2 pa-0">
								<v-btn class="rounded-pill" color="primary" width="150px" height="35px" variant="flat"
									:disabled="isDisabledUploadBtn" @click="upload">
									{{ $t("UPLOAD") }}
								</v-btn>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
			</v-container>
		</v-card-text>
		<!-- tiff のデータセットが選ばれているときは、取込の前に種別と取込単位を確定する（GI-4 / GI-5）。
		     取込本体は既存の一括ループに載せるため、フローには取込を任せず confirm で受ける（GI-20） -->
		<dtm-ortho-type-flow v-if="pendingDtmOrthoPair" :pair="pendingDtmOrthoPair" :upload-on-confirm="false"
			@confirm="onDtmOrthoConfirm" @previous="cancelDtmOrthoTypeSelection" @cancel="cancelDtmOrthoTypeSelection" />
	</v-card>
</template>

<script>
import DtmOrthoTypeFlow from "@/components/AddData/DtmOrthoTypeFlow.vue";
import { importDtmOrthoPair } from "@/components/AddData/dtm-ortho/dtmOrthoImport";
import { isLasPointCloudFile } from "@/components/AddData/uploadTypeResolver";
import { DTM_ORTHO_DATASET_REJECTION_KEYS } from "@/components/Project/dashboardImport.types";
import { getDtmOrthoDatasetList } from "@/components/Project/dashboardImportApi";
import {
	convertDatasetToFilePair,
	convertDtmOrthoDatasetResponse,
} from "@/components/Project/dashboardNodeAdapter";
import CloseButton from "@/components/common/CloseButton.vue";
import { useUploadTracker } from "@/composables/useUploadTracker";
import { importDesign } from "@/utils/designApiUtils";
import { handleUploadResults } from "@/utils/notificationUtils";
import { importPointCloud } from "@/utils/pointCloudApiUtils";
import axios from "axios";
import { mapMutations } from "vuex";

/** 点群カテゴリに混ぜる DTM / オルソのデータセットの type（既存要素は survey / preconstruction） */
const DTM_ORTHO_ITEM_TYPE = "dtmOrtho";

/** 許可拡張子の配列を既存の直書きと同じ見た目のラベルにする */
const toExtensionLabel = (extensions) => `( ${extensions.join(", ")} )`;

export default {
	components: {
		CloseButton,
		DtmOrthoTypeFlow,
	},
	props: {
		isShow: Boolean,
	},
	data() {
		return {
			axisList: [
				"ENZ",
				"NEZ",
				"ENZRGB",
				"NEZRGB",
				"ENZiRGB",
				"NEZiRGB",
				"ENZRGBi",
				"NEZRGBi",
			],
			axis: "ENZ",
			selectedPoints: [],
			selectedDesigns: [],
			selectedLineworks: [],
			pointCloudBucketId: {
				survey: "",
				preconstruction: "",
			},
			surveyPointCloudList: [],
			preconstructionPointCloudList: [],
			designBucketId: "",
			designList: [],
			/** GET /v2/api/:siteId/dtm-ortho/dashboard-datasets の datasets（点群カテゴリの一覧に混ぜる。GI-19） */
			dtmOrthoDatasetList: [],
			/** 同応答に同梱された許可拡張子。null のときラベルを描かない（GI-31） */
			allowedExtensions: null,
			/** 種別選択ダイアログを開いている間だけ保持する組 */
			pendingDtmOrthoPair: null,
			isPointCloudChecked: false,
			isDesignChecked: false,
			isLineworksChecked: false,
			date: "",
			time: "",
		};
	},
	computed: {
		/**
		 * ダウンロードボタンの活性制御
		 */
		isDisabledUploadBtn() {
			return (
				!this.selectedPoints.length &&
				!this.selectedDesigns.length &&
				!this.selectedLineworks.length
			);
		},
		/**
		 * 軸の可視制御
		 */
		isAxisEnabled() {
			if (!this.selectedPoints.length) {
				return false;
			}
			// tiff のデータセットに軸の概念は無い（点群が選ばれているときの判定は変えない）
			if (this.selectedPoints[0].type === DTM_ORTHO_ITEM_TYPE) {
				return false;
			}
			// 暫定対応で点群1件ずつしかアップロードできないため1件目だけチェックする
			// TODO: 恒久対応で複数点群のアップロードが可能になった場合選択された点群ごとに軸の要否のチェックを行うようにする
			// 拡張子がlas/lazの場合は軸は非表示
			return !isLasPointCloudFile(this.selectedPoints[0].name);
		},
		pointCloudList() {
			// surveyとpreconstructionの点群リストを昇順で並べる。
			// DTM / オルソのデータセットも同じ規則で混ぜる（独立カテゴリは作らない。GI-19）
			const sortedPointCloudList = [
				...this.surveyPointCloudList,
				...this.preconstructionPointCloudList,
				...this.dtmOrthoDatasetList.map((dataset) => ({
					type: DTM_ORTHO_ITEM_TYPE,
					...dataset,
				})),
			].sort((a, b) => Number(a.created_at) - Number(b.created_at));
			return sortedPointCloudList;
		},
		/** 点群カテゴリのラベルは既存の点群と tiff の拡張子を連結する。応答が無ければ空（GI-31） */
		pointCloudExtensionLabel() {
			if (!this.allowedExtensions) return "";
			return toExtensionLabel([
				...this.allowedExtensions.pointCloud,
				...this.allowedExtensions.dtmOrtho,
			]);
		},
		designExtensionLabel() {
			if (!this.allowedExtensions) return "";
			return toExtensionLabel(this.allowedExtensions.design);
		},
	},
	watch: {
		isShow(newValue) {
			if (newValue) {
				this.setItemList();
			}
		},
		isPointCloudChecked(newValue) {
			// チェックがオフになったら点群データの選択をクリア
			if (!newValue) this.selectedPoints = [];
		},
		isDesignChecked(newValue) {
			// チェックがオフになったら設計データの選択をクリア
			if (!newValue) this.selectedDesigns = [];
		},
		isLineworksChecked(newValue) {
			// チェックがオフになったらラインワークスデータの選択をクリア
			if (!newValue) this.selectedLineworks = [];
		},
	},
	methods: {
		...mapMutations(["set_snackbar"]),
		resetProperty() {
			(this.selectedPoints = []),
				(this.selectedDesigns = []),
				(this.selectedLineworks = []),
				(this.pointCloudBucketId = {
					survey: "",
					preconstruction: "",
				}),
				(this.surveyPointCloudList = []),
				(this.surveyPointCloudList = []),
				(this.designBucketId = ""),
				(this.designList = []),
				(this.dtmOrthoDatasetList = []),
				(this.allowedExtensions = null),
				(this.pendingDtmOrthoPair = null),
				(this.isPointCloudChecked = false),
				(this.isDesignChecked = false),
				(this.isLineworksChecked = false),
				(this.date = ""),
				(this.time = "");
		},
		async getSurveyPointSCloudList() {
			const URL = `${import.meta.env.VITE_API_BASE}/assetLink/survey/${this.$route.query.siteId}`;
			const res = await axios.get(URL);
			this.pointCloudBucketId.survey = res.data.bucket_id;
			if (res.data.nodes) {
				this.surveyPointCloudList = res.data.nodes.map((node) => ({
					type: "survey",
					...node,
				}));
			}
		},
		async getPreconstructionPointSCloudList() {
			const URL = `${import.meta.env.VITE_API_BASE}/assetLink/preconstruction/${this.$route.query.siteId}`;
			const res = await axios.get(URL);
			this.pointCloudBucketId.preconstruction = res.data.bucket_id;
			if (res.data.nodes) {
				this.preconstructionPointCloudList = res.data.nodes.map((node) => ({
					type: "preconstruction",
					...node,
				}));
			}
		},
		async getDesignList() {
			const URL = `${import.meta.env.VITE_API_BASE}/assetLink/design/${this.$route.query.siteId}`;
			const res = await axios.get(URL);
			this.designBucketId = res.data.bucket_id;
			this.designList = res.data.nodes;
		},
		/**
		 * DTM / オルソのデータセット一覧と拡張子ラベルを取得する。
		 * 失敗はここで握り、既存カテゴリの一覧表示を妨げない（GI-40）
		 */
		async getDtmOrthoDatasetList() {
			try {
				const response = await getDtmOrthoDatasetList(this.$route.query.siteId);
				const { datasets, extensions } =
					convertDtmOrthoDatasetResponse(response);
				this.dtmOrthoDatasetList = datasets;
				this.allowedExtensions = extensions;
			} catch (error) {
				console.error("Failed to get the DTM/ortho dataset list", error);
				this.dtmOrthoDatasetList = [];
				this.allowedExtensions = null;
			}
		},
		async pointCloudUpload(point) {
			const siteId = this.$route.query.siteId;

			const importData = {
				name: point.name,
				date: this.date,
				time: this.time,
				file_name: point.name,
				bucket_id: this.pointCloudBucketId[point.type],
				node_id: point.id,
			};

			if (this.isAxisEnabled) {
				importData.axis = this.axis;
			}

			const { point_cloud_id } = await importPointCloud(siteId, importData);
			return point_cloud_id;
		},
		async designUpload(design) {
			const siteId = this.$route.query.siteId;
			const { design_id } = await importDesign(siteId, {
				bucket_id: this.designBucketId,
				node_id: design.id,
				name: design.name,
				date: this.date,
				time: this.time,
			});
			return design_id;
		},
		async lineworksUpload(lineworks) {
			const URL = `${import.meta.env.VITE_API_BASE}/assetLink/lineworks/${this.$route.query.siteId}`;
			const data = {
				bucket_id: this.designBucketId,
				node_id: lineworks.id,
				name: lineworks.name,
				// Fallback unit for DXF without $INSUNITS
				site_distance_unit: this.$store.state.site?.units?.distance,
			};
			const res = await axios.post(URL, data);
			return res.data.lineworks_id;
		},
		/** tiff 2 件を 1 回の取込要求として開始する（手動 / Groupware と同じ手順。GI-7 / GI-20） */
		dtmOrthoUpload(input) {
			return importDtmOrthoPair(this.$route.query.siteId, input);
		},
		deselectPoint(id) {
			this.selectedPoints = this.selectedPoints.filter(
				(item) => id !== item.id,
			);
		},
		deselectDesign(id) {
			this.selectedDesigns = this.selectedDesigns.filter(
				(item) => id !== item.id,
			);
		},
		deselectLineworks(id) {
			this.selectedLineworks = this.selectedLineworks.filter(
				(item) => id !== item.id,
			);
		},
		setUploadDateTime() {
			// 現在の日時を取得
			const now = new Date();
			// 日付を yyyy-mm-dd 形式でフォーマット
			const year = now.getFullYear();
			const month = (now.getMonth() + 1).toString().padStart(2, "0");
			const day = now.getDate().toString().padStart(2, "0");
			this.date = `${year}-${month}-${day}`;
			// 時刻を hh:mm:00 形式でフォーマット
			const hours = now.getHours().toString().padStart(2, "0");
			const minutes = now.getMinutes().toString().padStart(2, "0");
			this.time = `${hours}:${minutes}:00`;
		},
		/**
		 * 「取込」のゲート。tiff のデータセットが選ばれていれば取込を開始せず先に種別選択ダイアログを開く。
		 * 選ばれていなければ現行と同じ手順に同期的に入る（GI-21 / GI-33）
		 */
		upload() {
			const dataset = this.selectedDtmOrthoDataset();
			if (dataset) {
				this.pendingDtmOrthoPair = convertDatasetToFilePair(dataset);
				return;
			}
			this.runUpload(null);
		},
		/** 点群カテゴリの選択から tiff のデータセットを取り出す（1 件制限のため 0 or 1 件） */
		selectedDtmOrthoDataset() {
			return this.selectedPoints.find(
				(item) => item.type === DTM_ORTHO_ITEM_TYPE,
			);
		},
		/**
		 * 種別選択の確定。フローが origin 付きに復元した割り当てをそのまま取込本体へ渡す
		 *
		 * @param {object} input 種別割り当てと取込単位（DtmOrthoImportInput）
		 */
		onDtmOrthoConfirm(input) {
			if (!this.pendingDtmOrthoPair) return;
			this.pendingDtmOrthoPair = null;
			this.runUpload(input);
		},
		/** 「前へ」「キャンセル」は一覧に戻るだけで取込を 1 件も開始しない */
		cancelDtmOrthoTypeSelection() {
			this.pendingDtmOrthoPair = null;
		},
		/**
		 * 取込本体。既存の並列ループはそのまま維持し、データセットがあるときだけ末尾に 1 要求を足す（GI-20 / GI-33）
		 *
		 * @param {object | null} dtmOrthoInput 種別割り当てと取込単位。null のときデータセットは取り込まない
		 */
		runUpload(dtmOrthoInput) {
			this.setUploadDateTime();
			// tiff のデータセットは既存の点群の要求（bucket_id をカテゴリのスロットから引く）に流さない
			const plainPoints = this.selectedPoints.filter(
				(item) => item.type !== DTM_ORTHO_ITEM_TYPE,
			);
			const dataset = dtmOrthoInput ? this.selectedDtmOrthoDataset() : null;
			// 選択項目からAPIリクエストリストを作成。種別は要求ごとに持たせ、結果との対応を添字に依存させない
			const uploadRequestList = [
				...plainPoints.map((item) => ({
					kind: "pointcloud",
					item,
					request: this.pointCloudUpload(item),
				})),
				...this.selectedDesigns.map((item) => ({
					kind: "design",
					item,
					request: this.designUpload(item),
				})),
				...this.selectedLineworks.map((item) => ({
					kind: "lineworks",
					item,
					request: this.lineworksUpload(item),
				})),
				...(dataset
					? [
							{
								kind: DTM_ORTHO_ITEM_TYPE,
								item: dataset,
								request: this.dtmOrthoUpload(dtmOrthoInput),
							},
						]
					: []),
			];

			const allItems = uploadRequestList.map(({ item }) => item);

			// リクエストを送信
			this.$store.commit("setLoading", true);
			Promise.allSettled(uploadRequestList.map(({ request }) => request))
				.then((results) => {
					const { trackItem } = useUploadTracker();
					const hasDataset = dataset != null;
					results.forEach((result, index) => {
						if (result.status === "fulfilled" && result.value != null) {
							const { kind } = uploadRequestList[index];
							if (kind === DTM_ORTHO_ITEM_TYPE) {
								// データセットは測量データと二次元図面の 2 行を追跡する
								trackItem(
									{ dataType: "pointcloud", id: result.value.pointCloudId },
									this.$route.query.siteId,
								);
								trackItem(
									{
										dataType: "drawing2dImage",
										id: result.value.drawing2dImageId,
									},
									this.$route.query.siteId,
								);
							} else {
								trackItem(
									{ dataType: kind, id: result.value },
									this.$route.query.siteId,
								);
							}
						}
					});
					handleUploadResults(this, results, allItems, () => {
						this.refreshDataList(hasDataset);
						// 正常終了の場合は閉じる
						this.$emit("close");
					});
				})
				.finally(() => {
					this.$store.commit("setLoading", false);
				});
		},
		setItemList() {
			this.getSurveyPointSCloudList();
			this.getPreconstructionPointSCloudList();
			this.getDesignList();
			this.getDtmOrthoDatasetList();
		},
		isDisabledPointCloudSelectItem(item) {
			// 組が成立しないデータセットは選択不可（GI-39）
			if (item.type === DTM_ORTHO_ITEM_TYPE && item.kind === "rejected") {
				return true;
			}
			// 点群ごとに軸を指定できないため、暫定対応で点群は1件しか選択できないようにする
			const isSelected = this.selectedPoints.find(
				(selected) => selected.id === item.id,
			);
			return !isSelected && this.selectedPoints.length > 0;
		},
		/** 選択不可の理由の i18n キー。理由が無い（選択可の）項目は null */
		rejectionMessageKey(item) {
			if (item.type !== DTM_ORTHO_ITEM_TYPE || item.kind !== "rejected") {
				return null;
			}
			return DTM_ORTHO_DATASET_REJECTION_KEYS[item.reason] ?? null;
		},
		/**
		 * @param {boolean} [importedDataset] データセットを取り込んだときだけ二次元図面一覧も更新する
		 */
		refreshDataList(importedDataset = false) {
			const siteId = this.$route.query.siteId;
			if (this.selectedPoints.length) {
				this.$store.dispatch("get_point_cloud_list", siteId);
			}
			if (this.selectedDesigns.length || this.selectedLineworks.length) {
				this.$store.dispatch("get_design_list", siteId);
			}
			if (importedDataset) {
				this.$store.dispatch("drawing2dImage/getDrawing2dImageList", siteId);
			}
		},
	},
	created() {
		this.setItemList();
	},
};
</script>

<style lang="sass" scoped>
:deep(.checkbox-label-size .v-label)
  font-size: 12px

.selected-list
  width:90%
  font-size: 14px

</style>